<template>
    <div>
        <SToolbar>
            <v-icon small class="mr-1">ic-20 ic-agenda ic-col-grey</v-icon>
            <v-toolbar-title>{{ $t('agenda') }}</v-toolbar-title>
        </SToolbar>
        <v-skeleton-loader v-if="!record" type="article, actions" />
        <v-card v-else height="var(--usable-height)" class="main-grid">
            <LoadingOverlay :value="saving" absolute />

            <!-- Header with Buttons -->
            <div class="top-header">
                <light-toolbar dense>
                    <v-toolbar-title>
                        {{ $t('veranstaltungen_erfassen', { event_class: $t(record.event_class || 'event') }) }}
                        {{ record.title ? `: ` : '' }}
                        <span v-if="record.title" class="record-title">{{ record.title }}</span>
                        <EventModeIndicator :event="record" class="ml-2" />
                    </v-toolbar-title>

                    <v-spacer />

                    <KpIcon :icon-cls="`${record.status_icon}`" color="grey" left />
                    <span class="grey--text">{{ $t(record.status) }}</span>
                </light-toolbar>
                <KpAlert
                    v-if="!recordSaveable()"
                    class="ma-2"
                    type="warning"
                    dismissible
                    icon="ic ic-24 ic-alert ic-col-white"
                >
                    {{ $t('info_event_not_saved') }}
                </KpAlert>
                <v-toolbar dense class="elevation-0" color="primary white--text">
                    <v-btn
                        v-if="!hidePreview"
                        color="primary"
                        depressed
                        :disabled="record.isPhantom()"
                        :href="getUrl(true)"
                        target="tgk_preview"
                    >
                        <KpIcon icon-cls="preview" color="white" disabled-color="grey" left />
                        {{ $t('preview') }}
                    </v-btn>
                </v-toolbar>
            </div>

            <!-- Navigation -->
            <div class="navigation">
                <v-tabs v-model="activeItem" vertical background-color="transparent" hide-slider>
                    <v-tab
                        v-for="item in navItems"
                        :key="item.id"
                        :disabled="record.isPhantom()"
                        exact
                        class="nav-tab"
                        :value="item.id"
                        :to="{ name: item.routeName }"
                    >
                        <KpIcon :icon-cls="`${item.icon}`" color="grey" active-color="black" left />
                        {{ item.titleFn ? item.titleFn() : item.title }}
                    </v-tab>
                </v-tabs>
            </div>

            <!-- Main content -->
            <div class="content">
                <router-view
                    ref="activeChild"
                    v-model="isValid"
                    :record="record"
                    @input="isValid = $event"
                    @saving="saving = $event"
                    @featured-image-created="onFeaturedImagesCreated"
                    @save="onSave"
                    @reload-event="reload"
                ></router-view>
            </div>

            <!-- footer -->
            <div class="bottom-toolbar d-flex justify-space-between">
                <PlainBtn :to="{ name: 'event-info-categories', params: { id: record.id } }">
                    {{ $t('switch_to_extended_view') }}
                </PlainBtn>
                <div class="d-flex justify-end">
                    <PlainBtn class="mr-1" @click="onCancel">
                        {{
                            backRoute?.displayText ? $t('back_to', { text: backRoute?.displayText }) : $t('schliessen')
                        }}
                    </PlainBtn>
                    <PlainBtn v-if="!firstItem" class="mr-1" :disabled="!isValid" @click="onBack">{{
                        $t('back')
                    }}</PlainBtn>
                    <ActionBtn v-if="showSaveBtn" :disabled="!isValid" class="mr-3" @click="onSave">
                        <KpIcon icon-cls="save" color="white" left />
                        {{ $t('save') }}
                    </ActionBtn>
                    <ActionBtn
                        v-if="!lastItem && showNextBtn"
                        :disabled="!isValid"
                        :loading="saving"
                        @click="onSaveAndNext"
                    >
                        <KpIcon icon-cls="save" color="white" left />{{ $t('save_next') }}
                    </ActionBtn>
                </div>
            </div>
        </v-card>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import { Event, EventCollection } from '@/models/Event';
import { errToStr, setBrowserTitle } from '@/helpers/tools';
import { mapActions } from 'vuex';
import { globalParam } from '@/helpers/globals';
import { userIsStaff, userIsOrganizer } from '@/helpers/auth';
import EventModeIndicator from '@/components/event/EventModeIndicator.vue';
import BackRouteMixin from '@/mixins/backroute';
import { getLocalValue, setLocalValue } from '@/helpers/localStorageHelper';
import { eventsOpenMode } from '@/helpers/eventsOpenMode';
import { commitToast } from '@/plugins/toast';

export default {
    name: 'EventExpressEdit',
    components: { EventModeIndicator },
    mixins: [BackRouteMixin],
    async beforeRouteUpdate(to, from, next) {
        await this.onSave();
        if (to.params.id !== from.params.id) {
            this.record = null;
        }
        this.loadRecord(to.params.id);
        next();
    },
    data() {
        return {
            saving: false,
            record: null,
            isValid: true,
            activeItem: null,
        };
    },
    computed: {
        ...mapState('user', {
            user: 'record',
        }),
        firstItem() {
            const routeName = this.$route.name;
            return this.navItems[0].routeName === routeName;
        },
        lastItem() {
            const routeName = this.$route.name;
            return this.navItems[this.navItems.length - 1].routeName === routeName;
        },
        showSaveBtn() {
            let actNavEntry = this.getActNavItemEntry();
            return actNavEntry && actNavEntry.showSaveBtn === true;
        },
        showNextBtn() {
            let actNavEntry = this.getActNavItemEntry();
            return actNavEntry && actNavEntry.showNextBtn === true;
        },
        hidePreview() {
            return globalParam('event.hide_preview');
        },
        isReviewByStaff() {
            return globalParam('event_review_by_staff');
        },
        showPublishView() {
            return this.userIsStaff() || !this.isReviewByStaff;
        },
        getAkGageView() {
            if (this.user.ak_module_active) {
                return {
                    id: 'gage',
                    routeName: 'event-express-gage',
                    icon: 'price',
                    title: this.$t('event_gage'),
                    showNextBtn: false,
                    showSaveBtn: true,
                };
            }
            return null;
        },
        getNavItemPanel() {
            if (!this.showPublishView) {
                return {
                    id: 'submit',
                    routeName: 'event-express-submit',
                    icon: 'published',
                    title: this.$t('submit'),
                    showNextBtn: false,
                    showSaveBtn: false,
                };
            }

            if (this.showPublishView) {
                return {
                    id: 'publish',
                    routeName: 'event-express-publish',
                    icon: 'published',
                    title: this.$t('publish'),
                    showNextBtn: false,
                    showSaveBtn: false,
                };
            }

            return null;
        },
        navItems() {
            return [
                {
                    id: 'info',
                    routeName: 'event-express-edit',
                    icon: 'info',
                    title: this.$t('info_description'),
                    showNextBtn: true,
                    showSaveBtn: true,
                },
                {
                    id: 'additional-info',
                    routeName: 'event-express-info',
                    icon: 'star',
                    title: this.$t('presales_prices'),
                    showNextBtn: true,
                    showSaveBtn: true,
                },
                // schedules: not all event classes have schedules:
                Event.CLASSES_WITH_SCHEDULES.indexOf(this.record?.event_class) >= 0
                    ? {
                          id: 'schedule',
                          routeName: 'event-express-schedule',
                          icon: 'calendar',
                          title: this.$t('schedule'),
                          showNextBtn: true,
                          showSaveBtn: true,
                      }
                    : null,
                // sub-events: not all event classes have sub-events:
                Event.CLASSES_WITH_SUB_EVENTS().indexOf(this.record?.event_class) >= 0
                    ? {
                          id: 'sub-events',
                          routeName: 'event-sub-events',
                          icon: 'subevent',
                          title: this.$t('sub_events'),
                          showNextBtn: true,
                          showSaveBtn: false,
                      }
                    : null,
                {
                    id: 'images',
                    routeName: 'event-express-edit-image',
                    icon: 'mediamanager',
                    title: this.$t('titelbild'),
                    showNextBtn: true,
                    showSaveBtn: false,
                },
                this.getNavItemPanel,
                this.getAkGageView,
            ].filter(Boolean);
        },
    },
    watch: {
        // Set the browser's title if the event's title changes:
        'record.title': function () {
            setBrowserTitle(this.getBrowserTitle());
        },
    },
    async beforeDestroy() {
        if (!this.record?.isPhantom()) {
            await this.onSave();
        }
    },
    mounted() {
        this.loadRecord(this.$route.params.id);
        eventsOpenMode[this.$route.params.id] = 'express';
        setLocalValue('events-open-mode', eventsOpenMode);
    },
    methods: {
        ...mapActions(['runAsync']),
        userIsStaff,
        userIsOrganizer,
        async loadRecord(id) {
            // create a new record if requested, or load an existing one:
            if (id === 'new' || !Number(id)) {
                this.record = new Event({
                    organizer_id: this.user.event_organizer_id,
                    location_id: this.user.event_organizer?.default_location_id,
                    event_organizer: this.user.event_organizer?.title,
                });
                // premature return in case of new record:
                return;
            }
            // load existing record:
            this.record = null;
            try {
                const collection = new EventCollection();
                await this.runAsync(() => collection.query({ id: id, includeOlder: true }));
                const record = collection.first();
                if (record) {
                    this.record = record;
                }
            } catch (e) {
                commitToast({ message: errToStr(e), color: 'error' });
            }
        },
        async reload() {
            this.loadRecord(this.record?.getId());
        },
        getActNavItemEntry() {
            const actRouteName = this.$route.name;
            const items = this.navItems.filter((i) => i.routeName === actRouteName);
            if (items && items.length > 0) {
                return items[0];
            }
            return null;
        },
        recordSaveable() {
            return this.record?.userCanSave(this.user);
        },
        async onSave() {
            if (!this.record) {
                return;
            }
            // Access check:
            if (!this.record.userCanSave(this.user)) {
                return false;
            }
            this.saving = true;
            // give the child a change to do things before we save:
            // e.g. save block contents:
            if (this.$refs.activeChild.beforeSave) {
                const res = await this.$refs.activeChild.beforeSave();
                if (res === false) {
                    this.saving = false;
                    return false;
                }
            }
            // nothing to do, so don't save:
            if (!this.record.isDirty() && !this.record.isPhantom()) {
                this.saving = false;
                return;
            }
            try {
                const isNew = !this.record.id;
                await this.runAsync(() => this.record.save());
                if (isNew) {
                    const eventsOpenMode = getLocalValue('events-open-mode', {});
                    eventsOpenMode[this.record.getId()] = 'express';
                    setLocalValue('events-open-mode', eventsOpenMode);
                    // when a new rec is created, replace the route...
                    this.$router.replace({ name: 'event-express-edit', params: { id: this.record.id } });
                }
            } catch (e) {
                commitToast({ message: errToStr(e), color: 'error' });
            } finally {
                this.saving = false;
            }
        },
        async onSaveAndNext() {
            await this.onSave();
            // find next nav item, based on the actual route, and navigate to it:
            const actRouteName = this.$route.name;
            const actItemEntry = this.navItems.filter((i) => i.routeName === actRouteName);
            if (actItemEntry.length > 0) {
                const nextIndex = this.navItems.indexOf(actItemEntry[0]) + 1;
                if (nextIndex > 0 && this.navItems.length > nextIndex) {
                    this.$router.push({ name: this.navItems[nextIndex].routeName });
                }
            }
        },
        onCancel() {
            if (this.getBackRouteConfig()?.route) {
                this.$router.push(this.getBackRouteConfig().route);
            } else {
                this.$router.push({ name: 'event' });
            }
        },
        async onBack() {
            await this.onSave();
            // find previous nav item, based on the actual route, and navigate to it:
            const actRouteName = this.$route.name;
            const actItemEntry = this.navItems.filter((i) => i.routeName === actRouteName);
            if (actItemEntry.length > 0) {
                const nextIndex = this.navItems.indexOf(actItemEntry[0]) - 1;
                if (nextIndex >= 0) {
                    this.$router.push({ name: this.navItems[nextIndex].routeName });
                }
            }
        },
        getUrl(preview = false) {
            if (preview) {
                return this.record?.detailPreviewUrl;
            } else {
                return this.record?.detailUrl;
            }
        },
        onFeaturedImagesCreated() {
            this.onSave();
        },
        getBrowserTitle() {
            if (this.record) {
                return `${this.record.title} - Agenda`;
            } else {
                return 'Agenda-Eingabe';
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.main-grid {
    display: grid;
    grid-template-rows: auto 1fr auto;
    grid-template-columns: 200px 1fr;
    grid-template-areas:
        'header header'
        'nav content'
        'nav bottom';
}

.top-header {
    grid-area: header;
}
.navigation {
    grid-area: nav;
    background-color: var(--toolbar-bg-color);
    padding: 12px;
    border-right: 1px solid var(--gray-border-color);
}
.content {
    grid-area: content;
    display: grid;
    grid-template-rows: minmax(0, 1fr);
    overflow: hidden;
}

.bottom-toolbar {
    grid-area: bottom;
    background-color: var(--toolbar-bg-color);
    padding: 10px 27px;
}

.nav-tab {
    height: initial !important;
    justify-content: start !important;
    padding: 5px 3px;
    margin: 3px;
    text-transform: none !important;
    font-weight: normal;
    font-size: 1rem;
    letter-spacing: 0;
    &.v-tab--active {
        color: var(--nearblack-color) !important;
    }
}
.record-title {
    color: initial;
}
</style>
